import React from 'react';
import { connect } from 'react-redux';

import { Team } from '@yandex-int/k-common/typings';
import { accessibleClick } from '@edu-frontend/modules/src/shared/lib/a11y';
import b from 'b_';
import { Checkbox, SimpleCallback, Text } from 'platform-components';
import { CHECKBOX_TYPES } from 'platform-components/constants';
import getStudentName from 'platform-components/src/utils/get-student-name';

import { getStudents } from 'common.components/classes/selectors';
import { Student } from 'common.components/typings';
import { getCurrentClass, getIsStudentRegistrationLinkAvailableInTeam } from 'common.components/user/selectors';
import { getTrimmedName } from 'utils/get-trimmed-name';
import { getTeamIdFromPath } from 'utils/path';

import { STUDENTS_ERRORS } from './typings';

import './students-list.scss';

interface StoreProps {
    currentTeam: Team | null;
    students: Array<Student> | null;
    inStudentLinkExperiment: boolean;
}

interface OwnProps {
    allStudentsDisabled?: boolean;
    controls?: React.ReactNode;
    error: STUDENTS_ERRORS | null;
    getIsStudentDisabled: (studentId: number) => boolean;
    onClick?: SimpleCallback;
    onToggleAllStudents?: (selected: Array<number>) => void;
    onToggleStudent?: (selected: Array<number>) => void;
    selected: Array<number>;
    showEmptyClassError?: boolean;
    showNoSelectedStudentsError?: boolean;
    shouldHideCheckbox: boolean;
}

export interface StudentsListProps extends StoreProps, OwnProps {}

export class StudentsListComponent extends React.Component<StudentsListProps> {
    static defaultProps = {
        getIsStudentDisabled: () => false,
    };

    toggleAllStudents = (): void => {
        const { onToggleAllStudents, selected, students } = this.props;

        if (!onToggleAllStudents) {
            return;
        }

        if (students?.length === selected.length) {
            onToggleAllStudents([]);
        } else {
            const studentsIds = students?.map((s) => s.id) || [];
            onToggleAllStudents(studentsIds);
        }
    };

    toggleStudent = (student: Student): void => {
        const { onToggleStudent, selected: selectedStudentIds } = this.props;

        if (!onToggleStudent) {
            return;
        }

        if (selectedStudentIds.some((id) => id === student.id)) {
            const selected = selectedStudentIds.filter((id) => id !== student.id);
            onToggleStudent(selected);
        } else {
            const selected = [...selectedStudentIds, student.id];
            onToggleStudent(selected);
        }
    };

    handleClick = () => {
        const { onClick } = this.props;

        if (onClick) {
            onClick();
        }
    };

    // eslint-disable-next-line complexity
    render() {
        const {
            allStudentsDisabled,
            controls,
            currentTeam,
            error,
            getIsStudentDisabled,
            onClick,
            shouldHideCheckbox,
            students,
            inStudentLinkExperiment,
        } = this.props;
        const { selected: selectedStudentIds } = this.props;

        const level = currentTeam && currentTeam.level;
        const name = currentTeam && currentTeam.name;

        const shouldPerformCustomAction = Boolean(onClick);

        const isEmptyTeam = students?.length === 0;
        const classTrimmedName = getTrimmedName(name);

        const showNoSelectedStudentsError = error === STUDENTS_ERRORS.NO_STUDENTS_SELECTED;
        const showEmptyTeamError = error === STUDENTS_ERRORS.EMPTY_TEAM;

        const className = b('students-list', {
            empty: isEmptyTeam,
            'with-action': shouldPerformCustomAction,
            'hidden-checkbox': shouldHideCheckbox,
        });

        return (
            <div className={className}>
                <div className="students-list__label">
                    {showNoSelectedStudentsError && (
                        <div className="students-list__no-students">
                            <Text id="assignLessonModal.errors.noSelectedStudents" />
                        </div>
                    )}
                </div>
                <div className="students-list__checkbox-wrapper" {...accessibleClick(this.handleClick)}>
                    {isEmptyTeam &&
                        (inStudentLinkExperiment ? (
                            <Text id="assignLessonModal.emptyTeam.studentLinkExperiment" type={Text.TYPE.MARKDOWN} />
                        ) : (
                            <Text id="assignLessonModal.emptyTeam" values={{ name: classTrimmedName, level }} />
                        ))}
                    {!isEmptyTeam && (
                        <Checkbox
                            checked={selectedStudentIds.length === students?.length}
                            disabled={shouldPerformCustomAction || allStudentsDisabled}
                            mix="students-list__checkbox qa_all-students-checkbox"
                            onChange={this.toggleAllStudents}
                            type={CHECKBOX_TYPES.BLUE}
                        >
                            <Text
                                id="lessonView.assignment.studentsPlaceholder.allStudents"
                                values={{ name: classTrimmedName, level }}
                                weight={Text.WEIGHT.M}
                            />
                        </Checkbox>
                    )}
                    <div className="students-list__list">
                        {students?.map((student, index) => (
                            <Checkbox
                                checked={selectedStudentIds.some((id) => id === student.id)}
                                disabled={shouldPerformCustomAction || getIsStudentDisabled(student.id)}
                                key={student.id}
                                mix="students-list__checkbox"
                                onChange={() => this.toggleStudent(student)}
                                type={CHECKBOX_TYPES.BLUE}
                            >
                                <span className="students-list__student-number">
                                    <Text>{index + 1}</Text>
                                </span>
                                <span className="qa_student-name">
                                    <Text>{getStudentName(student.firstName, student.lastName)}</Text>
                                </span>
                            </Checkbox>
                        ))}
                    </div>
                    {controls}
                    {showEmptyTeamError && (
                        <div className="students-list__empty-class">
                            <Text id="assignLessonModal.errors.emptyClass" />
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

const mapStateToProps = (storeState: any): StoreProps => {
    const classId = getTeamIdFromPath();

    return {
        currentTeam: getCurrentClass(storeState, { classId }),
        students: getStudents(storeState, { classId }),
        inStudentLinkExperiment: getIsStudentRegistrationLinkAvailableInTeam(storeState, { classId }),
    };
};

export const StudentsList = connect(mapStateToProps)(StudentsListComponent);
